import React, { useState } from 'react';
import {
    SUBSCRIPTION_STATUSES,
    formatAmount,
    formatDate,
    daysUntil,
    relativeDay,
    cycleLabel,
    cyclePerLabel,
    isRecurring,
    cardLabel,
    cardExpiry,
    costCentreLabel
} from './paymentConstants';
import ServiceAvatar from './ServiceAvatar';

// One record, as a scannable row: what it is on the left, what it costs on the
// right, and the action you most likely came for in the eye line. The audit
// trail and the rarer actions live behind "Details" so the common path stays
// uncluttered.
//
// Auto-renewal is shown on the face of the card, never only in the edit form —
// "will this take my money by itself?" is the question this module exists to
// answer, and hiding it behind a click defeats the point.

const renewalBadge = (payment) => {
    if (!isRecurring(payment)) return null;
    if (!['active', 'trial'].includes(payment.status)) return null;

    const days = daysUntil(payment.nextDueDate);
    if (days === null) return null;

    // Past the date on an auto-renewing service = the card was charged, it
    // just has not been confirmed. Only a manual one is genuinely overdue.
    if (days < 0 && payment.autoRenew !== false) return <span className="status-badge status-info">Charged? Confirm</span>;
    if (days < 0) return <span className="status-badge status-overdue">{relativeDay(days)}</span>;
    if (days === 0) return <span className="status-badge status-overdue">Renews today</span>;
    if (days <= 7) return <span className="status-badge status-due-soon">{relativeDay(days)}</span>;
    return <span className="status-badge status-active">{relativeDay(days)}</span>;
};

const PaymentCard = ({ payment, canCreate, canEdit, canDelete, onPay, onConfirm, onHistory, onEdit, onDelete, onCancel }) => {
    const [showDetails, setShowDetails] = useState(false);

    const recurring = isRecurring(payment);
    const isVariable = payment.paymentType === 'usage';
    const isOneTime = payment.paymentType === 'onetime';
    const isLive = ['active', 'trial'].includes(payment.status);

    const category = payment.category || {};
    const categoryName = category.name || payment.categoryName;
    const statusMeta = SUBSCRIPTION_STATUSES[payment.status] || SUBSCRIPTION_STATUSES.active;

    const subline = [payment.vendor, categoryName].filter(Boolean).join(' · ');
    const card = payment.card && typeof payment.card === 'object' ? payment.card : null;
    const expiry = cardExpiry(card);
    const branch = costCentreLabel(payment);
    const days = recurring ? daysUntil(payment.nextDueDate) : null;
    // One-click confirm: an auto-renewing service on or past its date
    const awaitingConfirm = recurring && isLive && payment.autoRenew !== false && days !== null && days <= 0;

    return (
        <div className={`pay-card ${!isLive ? 'pay-card-muted' : ''}`}>
            <div className="pay-card-row">
                <ServiceAvatar record={payment} />

                <div className="pay-card-main">
                    <div className="pay-card-title-row">
                        <span className="pay-card-name">{payment.name}</span>
                        {payment.status === 'trial' && <span className="status-badge status-trial">Free trial</span>}
                        {!isLive && <span className={`status-badge status-${statusMeta.tone}`}>{statusMeta.label}</span>}
                        {renewalBadge(payment)}
                    </div>
                    {subline && <div className="pay-card-sub">{subline}</div>}
                    {(card || payment.paymentMethod || branch) && (
                        <div className="pay-card-tags">
                            {card ? (
                                <span className={`pay-tag ${expiry?.expired ? 'pay-tag-danger' : expiry?.soon ? 'pay-tag-warning' : ''}`}
                                      title={expiry ? `Card expires ${expiry.text}` : undefined}>
                                    💳 {cardLabel(card)}{expiry?.expired ? ' · expired' : expiry?.soon ? ` · exp ${expiry.text}` : ''}
                                </span>
                            ) : payment.paymentMethod ? (
                                <span className="pay-tag">💳 {payment.paymentMethod}</span>
                            ) : null}
                            {branch && <span className="pay-tag">🏢 {branch}</span>}
                        </div>
                    )}
                    <div className="pay-card-sub pay-card-when">
                        {recurring && isLive
                            ? `${cycleLabel(payment)} · next ${formatDate(payment.nextDueDate)}`
                            : isOneTime
                                ? `Paid ${formatDate(payment.lastPaidDate)}`
                                : payment.lastPaidDate
                                    ? `Last paid ${formatDate(payment.lastPaidDate)}`
                                    : 'No payments recorded yet'}
                    </div>
                </div>

                {/* The single most consequential fact about a subscription */}
                {recurring && isLive && (
                    <div className={`autorenew-pill ${payment.autoRenew !== false ? 'autorenew-on' : 'autorenew-off'}`}
                         title={payment.autoRenew !== false
                             ? 'Will be charged automatically on the renewal date'
                             : 'Will not renew by itself — needs manual payment'}>
                        <span className="autorenew-dot" />
                        Auto-renew {payment.autoRenew !== false ? 'ON' : 'OFF'}
                    </div>
                )}

                <div className="pay-card-amount">
                    <div className="pay-card-amount-value">
                        {payment.amount != null
                            ? formatAmount(payment.amount, payment.currency)
                            : <span className="pay-card-amount-empty">Varies</span>}
                    </div>
                    <div className="pay-card-amount-sub">
                        {recurring ? cyclePerLabel(payment) : (isVariable && payment.amount != null ? 'typical' : '')}
                    </div>
                </div>

                <div className="pay-card-actions">
                    {canCreate && awaitingConfirm && (
                        <button
                            className="btn btn-sm btn-success"
                            onClick={() => onConfirm(payment)}
                            title={`Record ${payment.amount != null ? formatAmount(payment.amount, payment.currency) : 'the charge'} on ${formatDate(payment.nextDueDate)}`}
                        >
                            ✓ Confirm
                        </button>
                    )}
                    {canCreate && isLive && !isOneTime && (
                        <button className={`btn btn-sm ${awaitingConfirm ? 'btn-secondary' : 'btn-success'}`} onClick={() => onPay(payment)}>
                            {awaitingConfirm ? 'Details…' : recurring ? 'Mark Paid' : 'Record charge'}
                        </button>
                    )}
                    {payment.billingUrl && (
                        <a className="btn btn-sm btn-ghost" href={payment.billingUrl} target="_blank" rel="noopener noreferrer" title="Open the vendor's billing page">
                            Billing ↗
                        </a>
                    )}
                    <button className="btn btn-sm btn-secondary" onClick={() => onHistory(payment)}>History</button>
                    <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => setShowDetails(v => !v)}
                        title="Details"
                        aria-expanded={showDetails}
                    >
                        {showDetails ? '▾' : '⋯'}
                    </button>
                </div>
            </div>

            {showDetails && (
                <div className="pay-card-details">
                    <div className="pay-card-details-info">
                        {payment.description && <div>{payment.description}</div>}
                        {payment.accountEmail && <div>Account: <strong>{payment.accountEmail}</strong></div>}
                        {payment.paymentMethod && <div>Paid with: {payment.paymentMethod}</div>}
                        {branch && <div>Charged to: {branch}</div>}
                        {payment.startDate && <div>Started {formatDate(payment.startDate)}</div>}
                        {recurring && (payment.notifyEmails || []).length > 0 && (
                            <div>
                                Reminds {payment.notifyEmails.join(', ')}
                                {(payment.reminderOffsets?.length
                                    ? ` — ${payment.reminderOffsets.join(', ')} days before`
                                    : ` — ${payment.reminderDaysBefore ?? 5} days before`)}
                            </div>
                        )}
                        {payment.status === 'cancelled' && (
                            <div className="audit-deleted">
                                Cancelled {formatDate(payment.cancelledAt)}
                                {payment.cancellationReason && ` — ${payment.cancellationReason}`}
                            </div>
                        )}
                        {payment.notes && <div className="pay-card-notes">{payment.notes}</div>}
                        <div className="audit-info">
                            <div>
                                Added by <strong>{payment.createdBy?.fullName || payment.createdByName || '—'}</strong>
                                {payment.createdByDepartment && ` (${payment.createdByDepartment})`}
                                {payment.createdAt && ` · ${formatDate(payment.createdAt)}`}
                            </div>
                            {payment.lastEditedBy && (
                                <div className="audit-edited">
                                    Edited by {payment.lastEditedBy?.fullName || payment.lastEditedByName}
                                    {payment.updatedAt && ` · ${formatDate(payment.updatedAt)}`}
                                </div>
                            )}
                            {payment.status === 'deleted' && (
                                <div className="audit-deleted">
                                    Deleted by {payment.deletedBy?.fullName || payment.deletedByName || '—'}
                                    {payment.deletedAt && ` · ${formatDate(payment.deletedAt)}`}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="pay-card-details-actions">
                        {canEdit && payment.status !== 'deleted' && (
                            <button className="btn btn-sm btn-secondary" onClick={() => onEdit(payment)}>Edit</button>
                        )}
                        {canEdit && recurring && isLive && (
                            <button className="btn btn-sm btn-warning" onClick={() => onCancel(payment)}>
                                Cancel subscription
                            </button>
                        )}
                        {canDelete && payment.status !== 'deleted' && (
                            <button className="btn btn-sm btn-danger" onClick={() => onDelete(payment)}>Delete</button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentCard;
